import React, { useMemo, useState } from 'react'
import sendbutton from './assets/home/sendbutton.png'
import sendingbutton from './assets/home/sendingbutton.png'
import Consts from './Const'
import './SupportForm.css'
import { AppType, mapAppTypeToServerAppType } from './Services/ServiceTypes'

type SupportType = 'question' | 'bug' | 'feature' | 'other'
type Priority = 'low' | 'medium' | 'high'

type FormState = {
  type: SupportType
  priority: Priority
  subject: string
  message: string
  email: string
  stepsToReproduce: string
  expected: string
  actual: string
  appVersion: string
  device: string
  consent: boolean
  attachments: File[]
}

const initialState: FormState = {
  type: 'question',
  priority: 'medium',
  subject: '',
  message: '',
  email: '',
  stepsToReproduce: '',
  expected: '',
  actual: '',
  appVersion: '',
  device: '',
  consent: true,
  attachments: [],
}

function validateEmail(email: string) {
  // simpel og praktisk
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

type Lang = 'dk' | 'us'

interface ISupportFormProps {
  lang: Lang
  appType: AppType
}

const gettext = (lang: Lang, usText: string) => {
  switch (usText) {
    case 'Question':
      return lang === 'dk' ? 'Spørgsmål' : usText
    case 'Bug':
      return lang === 'dk' ? 'Fejl' : usText
    case 'Feature request':
      return lang === 'dk' ? 'Feature ønske' : usText
    case 'Other':
      return lang === 'dk' ? 'Andet' : usText
    case 'your@email.com':
      return lang === 'dk' ? 'din@email.dk' : usText
    case 'Email is required':
      return lang === 'dk' ? 'Email er påkrævet' : usText
    case 'Invalid email':
      return lang === 'dk' ? 'Ugyldig email' : usText
    case 'Subject required':
      return lang === 'dk' ? 'Emne er påkrævet' : usText
    case 'Description required':
      return lang === 'dk' ? 'Beskrivelse er påkrævet' : usText

    case 'Steps to reproduce bug is required':
      return lang === 'dk' ? 'Trin til at genskabe bug er påkrævet' : usText
    case 'Expected result is required':
      return lang === 'dk' ? 'Forventet resultat er påkrævet' : usText
    case 'Actual result is required':
      return lang === 'dk' ? 'Faktisk resultat er påkrævet' : usText

    case 'You must accept to submit':
      return lang === 'dk' ? 'Du skal acceptere for at sende' : usText

    case 'Please correct the errors in the form.':
      return lang === 'dk' ? 'Ret venligst fejlene i formularen.' : usText

    case 'Subject':
      return lang === 'dk' ? 'Emne' : usText
    case 'Description':
      return lang === 'dk' ? 'Beskrivelse' : usText
    case 'Enter the subject of your inquiry…':
      return lang === 'dk' ? 'Angiv emnet for din henvendelse…' : usText
    case 'Describe your inquiry…':
      return lang === 'dk' ? 'Beskriv din henvendelse…' : usText

    case 'Thank you! We have received your inquiry.':
      return lang === 'dk' ? 'Tak! Vi har modtaget din henvendelse.' : usText

    case 'Contact support':
      return lang === 'dk' ? 'Kontakt support' : usText

    case 'I agree that support may process my information in order to assist me':
      return lang === 'dk' ? 'Jeg accepterer at support må behandle mine oplysninger for at hjælpe mig' : usText

    case 'Sending...':
      return lang === 'dk' ? 'Sender...' : usText

    case 'Steps to reproduce':
      return lang === 'dk' ? 'Trin til at genskabe' : usText
    case 'Expected':
      return lang === 'dk' ? 'Forventet' : usText
    case 'Actual':
      return lang === 'dk' ? 'Faktisk' : usText

    case 'Unit / browser (optional)':
      return lang === 'dk' ? 'Enhed / browser (valgfrit)' : usText
    case 'e.g. iPhone 15 / Safari, Windows / Chrome':
      return lang === 'dk' ? 'fx iPhone 15 / Safari, Windows / Chrome' : usText

    case 'Submit error':
      return lang === 'dk' ? 'Submit fejl' : usText

    case 'Something went wrong. Try again.':
      return lang === 'dk' ? 'Noget gik galt. Prøv igen.' : usText

    case 'Bug-details':
      return lang === 'dk' ? 'Bug-detaljer' : usText

    default:
      return '???'
  }
}

export function SupportForm({ lang, appType }: ISupportFormProps) {
  const [state, setState] = useState<FormState>(initialState)
  const [submitting, setSubmitting] = useState(false)
  const [submittedId, setSubmittedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isBug = state.type === 'bug'

  const validation = useMemo(() => {
    const errors: Record<string, string> = {}

    if (!state.subject.trim()) errors.subject = gettext(lang, 'Subject required')
    if (!state.message.trim()) errors.message = gettext(lang, 'Description required')
    if (!state.email.trim()) errors.email = gettext(lang, 'Email is required')
    else if (!validateEmail(state.email)) errors.email = gettext(lang, 'Invalid email')

    if (isBug) {
      if (!state.stepsToReproduce.trim()) errors.stepsToReproduce = gettext(lang, 'Steps to reproduce bug is required')
      if (!state.expected.trim()) errors.expected = gettext(lang, 'Expected result is required')
      if (!state.actual.trim()) errors.actual = gettext(lang, 'Actual result is required')
    }

    // eksempel: kræv samtykke hvis du gemmer persondata
    if (!state.consent) errors.consent = gettext(lang, 'You must accept to submit')

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    }
  }, [state, isBug])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState(s => ({ ...s, [key]: value }))
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (!validation.isValid) {
      setError(gettext(lang, 'Please correct the errors in the form.'))
      return
    }

    setSubmitting(true)
    try {
      // --- VIGTIGT ---
      // Hvis du vil sende filer, brug FormData.
      //   const formData = new FormData()
      //   formData.append('type', state.type)
      //   //   formData.append('priority', state.priority)
      //   formData.append('subject', state.subject)
      //   formData.append('message', state.message)
      //   formData.append('email', state.email)
      //   //   formData.append('appVersion', state.appVersion)
      //   formData.append('device', state.device)
      //   formData.append('consent', String(state.consent))

      //   if (isBug) {
      //     formData.append('stepsToReproduce', state.stepsToReproduce)
      //     formData.append('expected', state.expected)
      //     formData.append('actual', state.actual)
      //   }

      //   state.attachments.forEach(file => {
      //     formData.append('attachments', file)
      //   })

      // Skift "/api/support" til dit endpoint:
      const res = await fetch(`${Consts.defaultBaseUrl}/support`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          AppType: mapAppTypeToServerAppType(appType),
          SupportType: state.type,
          Subject: state.subject,
          Message: state.message,
          Email: state.email,
          StepsToReproduce: state.stepsToReproduce,
          Expected: state.expected,
          Actual: state.actual,
          Device: state.device,
          Consent: state.consent,
        }),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        //`Server fejl (${res.status})`
        throw new Error(text || `${gettext(lang, `Submit error`)} (${res.status})`)
      }

      // forventer fx { id: "TICKET-123" }
      const data = (await res.json().catch(() => null)) as { id?: string } | null

      setSubmittedId(data?.id ?? 'OK')
      setState(initialState)
    } catch (err: any) {
      setError(err?.message ?? gettext(lang, 'Something went wrong. Try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 16 }}>
      {/* <h2>{gettext(lang, 'Contact support')}</h2> */}

      <form onSubmit={onSubmit} noValidate>
        <div className="support_type_email" style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span className="support_type">Type</span>
            <select
              className="support_type"
              value={state.type}
              onChange={e => update('type', e.target.value as SupportType)}
              style={{ width: '100%', padding: 10, marginTop: 6, boxSizing: 'border-box' }}
            >
              <option value="question">{gettext(lang, 'Question')}</option>
              <option value="bug">{gettext(lang, 'Bug')}</option>
              <option value="feature">{gettext(lang, 'Feature request')}</option>
              <option value="other">{gettext(lang, 'Other')}</option>
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span className="support_email">Email *</span>
            <input
              className="support_email"
              value={state.email}
              onChange={e => update('email', e.target.value)}
              type="email"
              placeholder={gettext(lang, 'your@email.com')}
              style={{ width: '100%', padding: 10, marginTop: 6, boxSizing: 'border-box' }}
            />
            {validation.errors.email && <div style={{ color: 'crimson', marginTop: 3 }}>{validation.errors.email}</div>}
          </label>
        </div>

        <label style={{ display: 'block', marginTop: 12 }}>
          <span className="support_subject">{gettext(lang, 'Subject')} *</span>
          <input
            className="support_subject"
            value={state.subject}
            onChange={e => update('subject', e.target.value)}
            placeholder={gettext(lang, 'Enter the subject of your inquiry…')}
            style={{ width: '100%', padding: 10, marginTop: 6, boxSizing: 'border-box' }}
          />
          {validation.errors.subject && <div style={{ color: 'crimson', marginTop: 3 }}>{validation.errors.subject}</div>}
        </label>

        <label style={{ display: 'block', marginTop: 12 }}>
          <span className="support_description">{gettext(lang, 'Description')} *</span>
          <textarea
            className="support_description"
            value={state.message}
            onChange={e => update('message', e.target.value)}
            placeholder={gettext(lang, 'Describe your inquiry…')}
            rows={6}
            style={{ width: '100%', padding: 10, marginTop: 6, boxSizing: 'border-box' }}
          />
          {validation.errors.message && <div style={{ color: 'crimson', marginTop: 3 }}>{validation.errors.message}</div>}
        </label>

        {isBug && (
          <div style={{ marginTop: 12, padding: 12, border: '1px dashed #ccc', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{gettext(lang, 'Bug-details')}</div>

            <label style={{ display: 'block', marginTop: 8 }}>
              <span className="support_description">{gettext(lang, 'Steps to reproduce')} *</span>
              <textarea
                className="support_description"
                value={state.stepsToReproduce}
                onChange={e => update('stepsToReproduce', e.target.value)}
                placeholder={'1) …\n2) …\n3) …'}
                rows={4}
                style={{ width: '100%', padding: 10, marginTop: 3, boxSizing: 'border-box' }}
              />
              {validation.errors.stepsToReproduce && <div style={{ color: 'crimson', marginTop: 6 }}>{validation.errors.stepsToReproduce}</div>}
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
              <label>
                <span className="support_description">{gettext(lang, 'Expected')} *</span>
                <textarea
                  className="support_description"
                  value={state.expected}
                  onChange={e => update('expected', e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: 10, marginTop: 3, boxSizing: 'border-box' }}
                />
                {validation.errors.expected && <div style={{ color: 'crimson', marginTop: 3 }}>{validation.errors.expected}</div>}
              </label>

              <label>
                <span className="support_description">{gettext(lang, 'Actual')} *</span>
                <textarea
                  className="support_description"
                  value={state.actual}
                  onChange={e => update('actual', e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: 10, marginTop: 3, boxSizing: 'border-box' }}
                />
                {validation.errors.actual && <div style={{ color: 'crimson', marginTop: 3 }}>{validation.errors.actual}</div>}
              </label>
            </div>

            <label style={{ display: 'block', marginTop: 10 }}>
              <span className="support_description">{gettext(lang, 'Unit / browser (optional)')} *</span>
              <input
                className="support_description"
                value={state.device}
                onChange={e => update('device', e.target.value)}
                placeholder={gettext(lang, 'e.g. iPhone 15 / Safari, Windows / Chrome')}
                style={{ width: '100%', padding: 10, marginTop: 3, boxSizing: 'border-box' }}
              />
            </label>
          </div>
        )}

        {/* <label style={{ display: 'block', marginTop: 12 }}>
          Vedhæft filer (valgfrit)
          <input
            type="file"
            multiple
            onChange={e => update('attachments', Array.from(e.target.files ?? []))}
            style={{ width: '100%', padding: 10, marginTop: 6 }}
          />
          {state.attachments.length > 0 && <div style={{ marginTop: 8, opacity: 0.85 }}>Vedhæftet: {state.attachments.map(f => f.name).join(', ')}</div>}
        </label> */}

        <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, marginLeft: 20, marginRight: 20 }}>
          <input type="checkbox" checked={state.consent} onChange={e => update('consent', e.target.checked)} />
          {gettext(lang, 'I agree that support may process my information in order to assist me')}
        </label>
        {validation.errors.consent && <div style={{ color: 'crimson', marginTop: 6 }}>{validation.errors.consent}</div>}

        <button
          type="submit"
          disabled={submitting}
          style={{ background: 'none', border: 'none', marginTop: 14, margin: '10px 14px', cursor: submitting ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? <img src={sendingbutton} /> : <img src={sendbutton} />}
        </button>
      </form>

      {!!submittedId && (
        <div style={{ padding: 12, border: '1px solid #ddd', borderRadius: 8, marginBottom: 12, color: 'green' }}>
          {gettext(lang, 'Thank you! We have received your inquiry.')}
          {/* <div style={{ marginTop: 6, opacity: 0.8 }}>Reference: {submittedId}</div> */}
        </div>
      )}

      {error && <div style={{ padding: 12, background: '#fff2f2', border: '1px solid #ffcccc', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
    </div>
  )
}
